import { PayloadAction } from '@reduxjs/toolkit';
import { InferSocketResponse, SentinelKey } from 'src/utils/socket';

interface UpsertOnePayload<K extends SentinelKey> {
  key: K;
  value: InferSocketResponse<K>;
}
/** An action that is dispatched when we receive an entity from the sentinel system. */
const upsertOne = <K extends SentinelKey>(
  payload: UpsertOnePayload<K>,
): PayloadAction<UpsertOnePayload<K>, 'SENTINEL.UPSERT_ONE'> => {
  return { type: upsertOne.type, payload };
};
upsertOne.type = 'SENTINEL.UPSERT_ONE' as const;

interface UpdateOnePartialPayload<K extends SentinelKey> {
  key: K;
  id: string;
  value: Partial<InferSocketResponse<K>>;
}
/** An action that is dispatched when we receive an updated entity from the sentinel system. */
const updateOnePartial = <K extends SentinelKey>(
  payload: UpdateOnePartialPayload<K>,
): PayloadAction<UpdateOnePartialPayload<K>, 'SENTINEL.UPDATE_ONE_PARTIAL'> => {
  return { type: updateOnePartial.type, payload };
};
updateOnePartial.type = 'SENTINEL.UPDATE_ONE_PARTIAL' as const;

interface RemoveOnePayload<K extends SentinelKey> {
  key: K;
  id: string;
}
/** An action that is dispatched when an entity is deleted from the sentinel system. */
const removeOne = <K extends SentinelKey>(
  payload: RemoveOnePayload<K>,
): PayloadAction<RemoveOnePayload<K>, 'SENTINEL.REMOVE_ONE'> => {
  return { type: removeOne.type, payload };
};
removeOne.type = 'SENTINEL.REMOVE_ONE' as const;

interface UpsertManyPayload<K extends SentinelKey> {
  key: K;
  value: Array<InferSocketResponse<K>>;
  query: string;
  responseMeta?: any;
}
/** An action that is dispatched when we receive a list of entities from the sentinel system. */
const upsertMany = <K extends SentinelKey>(
  payload: UpsertManyPayload<K>,
): PayloadAction<UpsertManyPayload<K>, 'SENTINEL.UPSERT_MANY'> => {
  return { type: upsertMany.type, payload };
};
upsertMany.type = 'SENTINEL.UPSERT_MANY' as const;

interface AppendOnePayload<K extends SentinelKey> {
  key: K;
  value: InferSocketResponse<K>;
  query: string | undefined;
}
/** An action that is dispatched when we receive an update to a list of entities from the sentinel system. */
const appendOne = <K extends SentinelKey>(
  payload: AppendOnePayload<K>,
): PayloadAction<AppendOnePayload<K>, 'SENTINEL.APPEND_ONE'> => {
  return { type: appendOne.type, payload };
};
appendOne.type = 'SENTINEL.APPEND_ONE' as const;

export const sentinelActions = {
  upsertOne,
  updateOnePartial,
  removeOne,
  upsertMany,
  appendOne,
};
